import type { LocationProvider } from './provider';
import type { DocCreationLocation } from './types';

function withTimeout<T>(
  signal: AbortSignal,
  timeout: number,
  action: (signal: AbortSignal) => Promise<T>
): Promise<T> {
  signal.throwIfAborted();
  return new Promise<T>((resolve, reject) => {
    const controller = new AbortController();
    const abort = () => controller.abort(signal.reason);
    const timer = setTimeout(
      () =>
        controller.abort(
          new DOMException('Location request timed out', 'TimeoutError')
        ),
      timeout
    );
    const cleanup = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', abort);
    };
    signal.addEventListener('abort', abort, { once: true });
    controller.signal.addEventListener(
      'abort',
      () => {
        cleanup();
        reject(controller.signal.reason);
      },
      { once: true }
    );
    Promise.resolve()
      .then(() => {
        controller.signal.throwIfAborted();
        return action(controller.signal);
      })
      .then(resolve, reject)
      .finally(cleanup);
  });
}

export function isDocCreationLocation(
  value: unknown
): value is DocCreationLocation {
  if (!value || typeof value !== 'object') return false;
  const p = value as DocCreationLocation;
  return (
    Number.isFinite(p.latitude) &&
    Math.abs(p.latitude) <= 90 &&
    Number.isFinite(p.longitude) &&
    Math.abs(p.longitude) <= 180 &&
    Number.isFinite(p.accuracy) &&
    p.accuracy >= 0 &&
    Number.isFinite(p.capturedAt) &&
    p.capturedAt > 0 &&
    p.capturedAt <= 8_640_000_000_000_000 &&
    (p.placeName === undefined || typeof p.placeName === 'string') &&
    (p.source === undefined || p.source === 'creation' || p.source === 'update')
  );
}

export interface LocationCaptureRequest {
  coordinates: Promise<DocCreationLocation | undefined>;
  result: Promise<DocCreationLocation | undefined>;
}

function isTransientPositionError(error: unknown) {
  if (!error || typeof error !== 'object') return false;
  const { code, name } = error as { code?: unknown; name?: unknown };
  // Browser position errors and Capacitor's Android/iOS location errors.
  return (
    name === 'TimeoutError' ||
    code === 2 ||
    code === 3 ||
    code === 'OS-PLUG-GLOC-0002' ||
    code === 'OS-PLUG-GLOC-0010'
  );
}

/** A creation-time capture shared by documents created during the same request. */
export class CreationLocationCapture {
  private enabled = true;
  private inFlight?: LocationCaptureRequest & {
    controller: AbortController;
  };

  constructor(
    private readonly provider: LocationProvider,
    private readonly getLanguage: () => string,
    private readonly fetcher: typeof fetch = (...args) => fetch(...args)
  ) {}

  setEnabled(enabled: boolean) {
    this.enabled = enabled;
    if (!enabled) {
      this.inFlight?.controller.abort();
      this.inFlight = undefined;
    }
  }

  capture(): Promise<DocCreationLocation | undefined> {
    return this.beginCapture().result;
  }

  beginCapture(): LocationCaptureRequest {
    if (!this.enabled) {
      const empty = Promise.resolve(undefined);
      return { coordinates: empty, result: empty };
    }
    if (this.inFlight) return this.inFlight;
    const controller = new AbortController();
    const coordinates = this.captureCoordinates(controller.signal).catch(
      () => undefined
    );
    const result = coordinates
      .then(location =>
        location ? this.enrichLocation(location, controller.signal) : undefined
      )
      .catch(() => undefined)
      .finally(() => {
        if (this.inFlight?.controller === controller) this.inFlight = undefined;
      });
    this.inFlight = { controller, coordinates, result };
    return this.inFlight;
  }

  private async captureCoordinates(
    signal: AbortSignal
  ): Promise<DocCreationLocation | undefined> {
    for (let attempt = 0; attempt < 2; attempt++) {
      signal.throwIfAborted();
      try {
        const position = await withTimeout(signal, 10_000, signal =>
          this.provider.getCurrentPosition(signal)
        );
        signal.throwIfAborted();
        if (!isDocCreationLocation(position)) return;
        // Only the platform fix is authoritative; never use API/IP coordinates.
        return {
          latitude: position.latitude,
          longitude: position.longitude,
          accuracy: position.accuracy,
          capturedAt: position.capturedAt,
        };
      } catch (error) {
        if (signal.aborted || attempt === 1 || !isTransientPositionError(error))
          throw error;
      }
    }
    return;
  }

  private async enrichLocation(
    coordinates: DocCreationLocation,
    signal: AbortSignal
  ) {
    const location = { ...coordinates };
    try {
      const url = new URL(
        'https://api.bigdatacloud.net/data/reverse-geocode-client'
      );
      url.searchParams.set('latitude', String(location.latitude));
      url.searchParams.set('longitude', String(location.longitude));
      url.searchParams.set(
        'localityLanguage',
        this.getLanguage().split(/[-_]/)[0] || 'en'
      );
      // Use only this creation request's fresh fix. Never look up a place
      // again from a location loaded from storage or another client.
      const place = await withTimeout(signal, 5_000, async signal => {
        const response = await this.fetcher(url, {
          signal,
          credentials: 'omit',
          referrerPolicy: 'no-referrer',
        });
        if (!response.ok) throw new Error('Place lookup failed');
        return (await response.json()) as Record<string, unknown>;
      });
      if (
        place &&
        !String(place.lookupSource ?? '')
          .toLowerCase()
          .includes('ip')
      ) {
        const parts = [
          place.city || place.locality,
          place.principalSubdivision,
          place.countryName,
        ];
        const placeName = [
          ...new Set(
            parts
              .filter(
                (part): part is string =>
                  typeof part === 'string' && !!part.trim()
              )
              .map(part => part.trim())
          ),
        ].join(', ');
        if (placeName) location.placeName = placeName;
      }
    } catch {
      // Offline/failed lookups still preserve the device fix.
    }
    signal.throwIfAborted();
    return location;
  }
}
